import { useEffect, useState } from "react";
import { AppShell } from "@/components/Nav";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, AlertTriangle, ArrowUpRight, PauseCircle, PercentCircle } from "lucide-react";
import { Link } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CancelSaveDialog } from "@/components/CancelSaveDialog";

export default function Billing() {
  const { refreshUser } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [resuming, setResuming] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/subscription/me");
      setSub(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const cancel = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post("/subscription/cancel");
      toast.success(data.message);
      if (refreshUser) await refreshUser();
      load();
      setSaveOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCancelling(false);
    }
  };

  const resume = async () => {
    setResuming(true);
    try {
      const { data } = await api.post("/subscription/resume");
      toast.success(data.message);
      if (refreshUser) await refreshUser();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResuming(false);
    }
  };

  if (loading) return <AppShell><div className="text-[#A8ADA5]">Loading…</div></AppShell>;

  const isPro = sub?.plan === "basic" || sub?.plan === "plus";
  const canCancelFree = sub?.can_cancel_free;
  const planLabel = sub?.plan === "plus" ? "Aura Plus" : sub?.plan === "basic" ? "Aura Basic" : "Free";

  return (
    <AppShell>
      <div className="mb-10">
        <div className="overline mb-3">Billing</div>
        <h1 className="font-display text-5xl md:text-6xl leading-none">Your plan.</h1>
        <p className="text-[#A8ADA5] mt-3 max-w-2xl">
          View your current plan, credits, and manage subscription.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-px bg-[#26282C] border border-[#26282C] mb-8">
        <div className="bg-[#0B0C0E] p-6">
          <div className="overline mb-3">Plan</div>
          <div className={`font-display text-4xl ${isPro ? "text-[#D5FF3F]" : "text-[#F5F3EE]"}`} data-testid="billing-plan">
            {planLabel}
          </div>
        </div>
        <div className="bg-[#0B0C0E] p-6">
          <div className="overline mb-3">Scans</div>
          <div className="ticker text-4xl text-[#F5F3EE]" data-testid="billing-credits">
            {isPro ? "∞" : "Scan only"}
          </div>
        </div>
        <div className="bg-[#0B0C0E] p-6">
          <div className="overline mb-3">Renews / ends</div>
          <div className="ticker text-lg text-[#F5F3EE]" data-testid="billing-until">
            {sub?.pro_until ? new Date(sub.pro_until).toLocaleDateString() : "—"}
          </div>
        </div>
      </div>

      {canCancelFree && (
        <div className="card-surface p-6 mb-6 border-[#D5FF3F]/40" data-testid="croa-window-notice">
          <div className="flex items-start gap-4">
            <ShieldCheck size={22} className="text-[#D5FF3F] flex-shrink-0 mt-1" />
            <div>
              <div className="text-[#F5F3EE] font-medium mb-1">3-day CROA rescission window is open</div>
              <p className="text-sm text-[#A8ADA5] leading-relaxed">
                You can cancel right now for a full refund, no questions asked. This right expires 72 hours after
                your purchase (per 15 U.S.C. § 1679e).
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Paused-state banner */}
      {sub?.subscription_resumes_at && (
        <div className="card-surface p-6 mb-6 border-[#7CFF9B]/40" data-testid="paused-banner">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <PauseCircle size={22} className="text-[#7CFF9B] flex-shrink-0 mt-1" />
              <div>
                <div className="text-[#F5F3EE] font-medium mb-1">Subscription paused</div>
                <p className="text-sm text-[#A8ADA5] leading-relaxed">
                  You won&apos;t be billed until{" "}
                  <span className="text-[#F5F3EE] font-mono">
                    {new Date(sub.subscription_resumes_at).toLocaleDateString()}
                  </span>
                  . You still have full access through your current paid period.
                </p>
              </div>
            </div>
            <Button
              onClick={resume}
              disabled={resuming}
              data-testid="resume-sub-btn"
              className="rounded-full btn-outline"
            >
              {resuming ? "Resuming…" : "Resume now"}
            </Button>
          </div>
        </div>
      )}

      {/* Discount-applied banner */}
      {sub?.save_discount_applied_at && (
        <div className="card-surface p-6 mb-6 border-[#D5FF3F]/40" data-testid="discount-banner">
          <div className="flex items-start gap-4">
            <PercentCircle size={22} className="text-[#D5FF3F] flex-shrink-0 mt-1" />
            <div>
              <div className="text-[#F5F3EE] font-medium mb-1">50% off applied to your next bill</div>
              <p className="text-sm text-[#A8ADA5] leading-relaxed">
                Enjoy — full price resumes the month after unless you cancel.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3 flex-wrap">
        <Link to="/app/pricing">
          <Button className="rounded-full btn-outline">
            {isPro ? "Change plan" : "Upgrade"} <ArrowUpRight size={15} className="ml-1.5" />
          </Button>
        </Link>

        {isPro && canCancelFree && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="cancel-sub-btn" className="rounded-full text-[#FF6B4A] hover:bg-[#FF6B4A]/10 bg-transparent">
                Cancel subscription
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#131518] border-[#26282C] text-[#F5F3EE]">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display text-3xl">
                  Cancel with full refund?
                </AlertDialogTitle>
                <AlertDialogDescription className="text-[#A8ADA5]">
                  You&apos;re within your <span className="text-[#D5FF3F]">3-day CROA rescission window</span>.
                  We&apos;ll cancel now and refund the full amount to your original payment method within 5–10
                  business days.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full btn-outline">Keep Pro</AlertDialogCancel>
                <AlertDialogAction
                  onClick={cancel}
                  disabled={cancelling}
                  data-testid="confirm-cancel-btn"
                  className="rounded-full bg-[#FF6B4A] text-white hover:bg-[#FF5A36]"
                >
                  {cancelling ? "Cancelling…" : "Confirm cancel"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {isPro && !canCancelFree && (
          <Button
            data-testid="cancel-sub-btn"
            onClick={() => setSaveOpen(true)}
            className="rounded-full text-[#FF6B4A] hover:bg-[#FF6B4A]/10 bg-transparent"
          >
            Cancel subscription
          </Button>
        )}
      </div>

      <CancelSaveDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        onCancel={cancel}
        onSaved={load}
        cancelling={cancelling}
      />

      <div className="mt-12 card-surface p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} className="text-[#6D716C] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[#6D716C] leading-relaxed">
            Aura Credit is a software / educational tool, not a credit repair organization in the traditional
            sense. We do not guarantee any specific credit-score improvement or item removal. You are entitled
            to a full refund within 3 days of any subscription purchase under CROA (15 U.S.C. § 1679e), and you
            may perform any dispute yourself, for free, at any time.
          </p>
        </div>
      </div>
    </AppShell>
  );
}
